import { cooperate, finish } from '@tol/core/work';
import * as Core from '@tol/core/core';
import type {
  Network,
  RouteDirection,
  RouteResponse,
  RouteStop,
  Station,
  StationId,
  StopSequence,
} from '@tol/core/types';
import type { GtfsNetworkFile } from './types.ts';

export const DIRECTION: readonly RouteDirection[] = ['outbound', 'inbound'];

export function gtfsNetwork(file: GtfsNetworkFile): Network {
  return finish(networkSteps(file));
}

export function gtfsNetworkAsync(file: GtfsNetworkFile, signal?: AbortSignal): Promise<Network> {
  return cooperate(networkSteps(file), signal);
}

function* networkSteps(file: GtfsNetworkFile): Generator<void, Network, void> {
  const responses: RouteResponse[] = [];
  const points = new Map<string, RouteStop>();
  for (const [id, s] of Object.entries(file.stations)) points.set(id, { id, name: s[0], lat: s[1], lon: s[2] });
  for (const [lineId, patterns] of Object.entries(file.lines)) {
    yield;
    for (const [dir, direction] of DIRECTION.entries()) {
      const stopPointSequences: StopSequence[] = patterns
        .filter((p) => p.dir === dir)
        .map((p) => ({
          direction,
          stopPoint: p.ids.flatMap((id) => {
            const s = points.get(id);
            return s ? [s] : [];
          }),
        }));
      if (stopPointSequences.length > 0) responses.push({ lineId, direction, data: { stopPointSequences } });
    }
  }
  const net = yield* Core.buildNetworkSteps(responses);
  const stations = new Map(
    [...net.stations].map(([id, st]: readonly [StationId, Station]) => [
      id,
      { ...st, name: file.stations[id]?.[0] ?? st.name },
    ]),
  );
  return { ...net, stations };
}

export function isNetworkFile(v: unknown): v is GtfsNetworkFile {
  return (
    typeof v === 'object' &&
    v !== null &&
    'stations' in v &&
    typeof v.stations === 'object' &&
    v.stations !== null &&
    'lines' in v &&
    typeof v.lines === 'object' &&
    v.lines !== null
  );
}

export const RAILROAD_REACH = 2;
